import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { navItems } from './NavigationItems'
import TicketBadge from './TicketBadge'
import { useAuthStore } from '../stores/authStore'
import { usePortalStore } from '../stores/portalStore'

const DRAWER_WIDTH = 260

interface SidebarProps {
  mobileOpen: boolean
  onClose: () => void
}

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { config } = usePortalStore()

  const userRole = user?.role || 'user'
  const visibleItems = navItems.filter((item) => item.roles.includes(userRole))
  const institutionName = config?.institutionName || 'Flexilabs'

  const sidebarColor = config?.sidebarColor || '#ffffff'
  const sidebarTextColor = config?.sidebarTextColor || '#1f2937'
  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'

  const drawerBg = glassmorphism
    ? `${sidebarColor}dd`
    : sidebarColor

  const selectedBg = glassmorphism
    ? `${theme.palette.primary.main}22`
    : theme.palette.primary.light + '22'

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2 }}>
        <Box sx={{ fontWeight: 700, fontSize: '1.1rem', color: glassmorphism ? sidebarTextColor : 'primary.main' }}>
          {institutionName}
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {visibleItems.map((item) => {
          const Icon = item.icon
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
          return (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => {
                navigate(item.path)
                if (!isDesktop) onClose()
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                transition: 'all 0.2s ease',
                color: sidebarTextColor,
                '&.Mui-selected': {
                  bgcolor: selectedBg,
                  color: glassmorphism ? theme.palette.primary.dark : 'primary.main',
                },
                '&:hover': {
                  bgcolor: glassmorphism ? `${theme.palette.primary.main}15` : 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                {item.path === '/admin/tickets' ? (
                  <TicketBadge><Icon fontSize="small" /></TicketBadge>
                ) : (
                  <Icon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    bgcolor: drawerBg,
    color: sidebarTextColor,
    ...(glassmorphism && {
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }),
  }

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      {isDesktop ? (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  )
}
